/**
 * Memoized selectors for linkups
 * Uses createSelector for performance optimization
 */

import { createSelector } from '@reduxjs/toolkit';
import {
  deriveCityFromAddress,
  resolveLinkupAttendeeCount,
  transformLinkupsForCards,
} from '../util/linkupData';

// Base selectors
export const selectLinkupsState = state => state.linkups || {};
export const selectRawLinkups = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.linkups || []
);
export const selectRawUserLinkups = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.userLinkups || []
);
export const selectRawLinkupDetails = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.linkupDetails || {}
);
export const selectUnreadCounts = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.unreadCounts || {}
);
export const selectTotalUnreadCount = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.totalUnreadCount || 0
);
export const selectLinkupsLoading = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.fetchLinkupsInProgress || false
);
export const selectUserLinkupsLoading = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.fetchUserLinkupsInProgress || false
);
export const selectLinkupsError = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.fetchLinkupsError || null
);
export const selectUserLinkupsError = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.fetchUserLinkupsError || null
);
export const selectCreateLinkupLoading = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.createLinkupInProgress || false
);
export const selectCreateLinkupError = createSelector(
  [selectLinkupsState],
  linkupsState => linkupsState.createLinkupError || null
);
export const selectLinkupsMetadata = createSelector([selectLinkupsState], linkupsState => ({
  linkupsLastFetched: linkupsState.linkupsLastFetched || null,
  linkupsQueryKey: linkupsState.linkupsQueryKey || null,
  userLinkupsLastFetched: linkupsState.userLinkupsLastFetched || null,
  userLinkupsUserId: linkupsState.userLinkupsUserId || null,
}));
export const selectDiscoverLinkupsCount = createSelector(
  [selectRawLinkups],
  linkups => linkups.length
);

const formatDateToMMDDYYYY = dateStr => {
  if (!dateStr) return '';

  try {
    const dateParts = dateStr.split('-');
    if (dateParts.length === 3) {
      const [year, month, day] = dateParts;
      return `${month}-${day}-${year}`;
    }
  } catch (error) {}

  return dateStr;
};

const enhanceJoinedLinkup = (linkUp, unreadCounts) => ({
  ...linkUp,
  lastMessage: linkUp.description || linkUp.location || 'Welcome to the LinkUp!',
  lastMessageTime: formatDateToMMDDYYYY(linkUp.date) || 'Just now',
  date: linkUp.date,
  displayDate: formatDateToMMDDYYYY(linkUp.date),
  time: linkUp.time,
  timezone: linkUp.timezone,
  unreadCount: unreadCounts[linkUp.id] || 0,
  isFavorite: false,
  isArchived: linkUp.status === 'archived' || linkUp.isArchived || false,
});

const toChatLinkup = rawLinkUp => {
  if (!rawLinkUp) {
    return null;
  }

  return {
    id: rawLinkUp.id,
    title: rawLinkUp.title || rawLinkUp.linkUp_name || 'LinkUp',
    description: rawLinkUp.description || rawLinkUp.linkUp_description || '',
    location:
      rawLinkUp.location || rawLinkUp.location_description || rawLinkUp.linkUp_address || '',
    locationDescription: rawLinkUp.locationDescription || rawLinkUp.location_description || '',
    linkUp_address: rawLinkUp.linkUp_address || '',
    city: rawLinkUp.city || deriveCityFromAddress(rawLinkUp.linkUp_address),
    date: rawLinkUp.date || rawLinkUp.linkUp_date || null,
    time: rawLinkUp.time || null,
    timezone: rawLinkUp.timezone || null,
    attendeeCount: resolveLinkupAttendeeCount(rawLinkUp),
    totalGoing: resolveLinkupAttendeeCount(rawLinkUp),
    members_count: resolveLinkupAttendeeCount(rawLinkUp),
    category: rawLinkUp.category || rawLinkUp.linkUp_category || 'General',
    status: rawLinkUp.status || rawLinkUp.linkUp_status || 'active',
    crewId: rawLinkUp.crewId || rawLinkUp.crew_id || null,
    createdBy: rawLinkUp.createdBy || rawLinkUp.created_by || null,
    fanLimit: rawLinkUp.fanLimit || rawLinkUp.linkUp_fan_limit || null,
    image: rawLinkUp.image || rawLinkUp.linkUp_header_image || '/assets/linkup.svg',
    isFavorite: !!rawLinkUp.isFavorite,
    isArchived:
      rawLinkUp.isArchived ||
      rawLinkUp.status === 'archived' ||
      rawLinkUp.linkUp_status === 'archived',
    linkUpType: rawLinkUp.linkUpType || rawLinkUp.linkUp_type || 'public',
  };
};

export const selectLinkupsForCards = createSelector([selectRawLinkups], linkups =>
  transformLinkupsForCards(linkups)
);

export const selectJoinedLinkUpIds = createSelector([selectRawUserLinkups], userLinkups =>
  userLinkups.map(linkup => linkup.id || linkup.LinkUp_id).filter(Boolean)
);

export const selectJoinedLinkUpsForChat = createSelector(
  [selectRawUserLinkups, selectUnreadCounts],
  (userLinkups, unreadCounts) => {
    if (!userLinkups.length) {
      return [];
    }

    return userLinkups.map(linkUp => enhanceJoinedLinkup(linkUp, unreadCounts));
  }
);

export const selectRouteSelectedLinkUp = createSelector(
  [
    selectJoinedLinkUpsForChat,
    selectRawLinkupDetails,
    selectRawLinkups,
    (state, linkUpId) => linkUpId,
  ],
  (joinedLinkUps, linkupDetails, rawLinkups, linkUpId) => {
    if (!linkUpId) {
      return null;
    }

    const normalizedId = String(linkUpId);
    const joinedMatch = joinedLinkUps.find(linkUp => String(linkUp.id) === normalizedId) || null;
    if (joinedMatch) {
      return joinedMatch;
    }

    const detailMatch = linkupDetails[normalizedId];
    if (detailMatch) {
      return toChatLinkup(detailMatch);
    }

    const discoverMatch = rawLinkups.find(linkUp => String(linkUp.id) === normalizedId) || null;
    return discoverMatch ? toChatLinkup(discoverMatch) : null;
  }
);
